import React, { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useParams } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { FormProvider } from "./context/FormContext";
import { SourceProvider, useSource } from "./context/SourceContext";
import { UploadStep } from "./components/UploadStep";
import { FormWizard } from "./components/FormWizard";
import { TrustBadge } from "./components/TrustBadge";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import PlatformNotice from "./pages/PlatformNotice";
import AdminDashboard from "./pages/AdminDashboard";
import ApplyDriveLanding from "./pages/ApplyDriveLanding";
import OperatorMode from "./pages/OperatorMode";
import CookieConsent from "./components/CookieConsent";
import { Car, Clock, ShieldCheck, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "./components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const trackManualStart = (sourceSlug) => {
  fetch(`${API}/track/manual-start`, { method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({ source: sourceSlug || null }) }).catch(() => {});
};

const Header = () => {
  const { source } = useSource();
  const logoSrc = source.logo_url || "https://customer-assets.emergentagent.com/job_datapop-form/artifacts/ugn7g1st_CM-Central-East-Transp.png";
  const primaryColor = source.primary_color || '#00a94f';
  
  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <img 
              src={logoSrc}
              alt={`${source.company_name || source.name} Logo`}
              className="h-12 w-auto"
              data-testid="header-logo"
            />
          </div>
          {(!source.slug || source.slug === 'carmoney-ce') ? (
            <a 
              href={source.website_url || "https://applydrive.uk"} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm hover:underline"
              style={{ color: primaryColor }}
              data-testid="visit-source-link"
            >
              Visit {source.name || 'ApplyDrive'}
            </a>
          ) : source.website_url ? (
            <a 
              href={source.website_url.startsWith('http') ? source.website_url : `https://${source.website_url}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm hover:underline"
              style={{ color: primaryColor }}
              data-testid="visit-source-link"
            >
              Visit {source.name}
            </a>
          ) : null}
        </div>
      </div>
    </header>
  );
};

const Footer = () => {
  const { source } = useSource();

  return (
    <footer className="bg-slate-900 text-white py-6 mt-auto">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-3">
          {source.disclosure_text && (
            <p className="text-xs text-slate-400 leading-relaxed whitespace-pre-line">
              {source.disclosure_text}
            </p>
          )}
          <div className="flex flex-wrap justify-center gap-4 pt-2 text-xs">
            {source.terms_url ? <a href={source.terms_url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white">T&C</a> : <span className="text-slate-600 cursor-default">T&C</span>}
            {source.privacy_policy_url ? <a href={source.privacy_policy_url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white">Privacy Policy</a> : <span className="text-slate-600 cursor-default">Privacy Policy</span>}
            {source.idd_url ? <a href={source.idd_url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white">Initial Disclosure</a> : <span className="text-slate-600 cursor-default">Initial Disclosure</span>}
            {source.complaints_url ? <a href={source.complaints_url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white">Complaints Procedure</a> : <span className="text-slate-600 cursor-default">Complaints Procedure</span>}
            <a href="/platform-notice" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white">Platform Notice</a>
          </div>
          <p className="text-xs text-slate-600 pt-2">
            Copyright © 2026 {source.name}, all rights reserved.
          </p>
          <a href="/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-xs text-slate-500 hover:text-slate-300 transition-colors" data-testid="powered-by-applydrive">
            <img src="https://customer-assets.emergentagent.com/job_658a2c5a-a06c-4211-b54e-d929806b87d8/artifacts/7wlqr7yi_ApplyDrive%20Background%20Removed.png" alt="ApplyDrive" className="h-4 w-auto opacity-50" />
            Powered by ApplyDrive.uk
          </a>
          <p className="text-[11px] text-slate-600 pt-3 leading-relaxed max-w-2xl mx-auto" data-testid="legal-entity-disclosure">
            ApplyDrive is a trading name of <strong className="text-slate-500">Phase23 Ltd</strong>, a company registered in England and Wales (Company No. <a href="https://find-and-update.company-information.service.gov.uk/company/17247792" target="_blank" rel="noopener noreferrer" className="hover:text-slate-400 underline-offset-2 hover:underline">17247792</a>). Registered office: 66 Paul Street, London, England, EC2A 4NA. ICO Registration: <a href="https://ico.org.uk/ESDWebPages/Entry/ZC161033" target="_blank" rel="noopener noreferrer" className="hover:text-slate-400 underline-offset-2 hover:underline">ZC161033</a>.
          </p>
        </div>
      </div>
    </footer>
  );
};

const LandingPage = ({ onGetStarted, onStartManual }) => {
  const { source } = useSource();
  const color = source.primary_color || '#00a94f';
  const hoverColor = source.primary_color ? `${source.primary_color}dd` : '#008c42';
  const companyName = source.name || source.company_name || 'ApplyDrive';
  const heroImage = source.landing_image_url || null;
  
  return (
    <div className="min-h-[80vh] flex items-center">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="text-center lg:text-center">
            <h1 
              className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="hero-title"
            >
              Apply for
              <span style={{ color }}> Vehicle Finance</span>
            </h1>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              Complete your vehicle finance application with <strong>{companyName}</strong> in just a few minutes. 
              Simply fill in your details and submit — it's quick, easy and secure.
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}18` }}>
                  <Clock className="w-5 h-5" style={{ color }} />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-slate-900 text-sm">Quick</p>
                  <p className="text-xs text-slate-500">Takes just minutes</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}18` }}>
                  <ShieldCheck className="w-5 h-5" style={{ color }} />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-slate-900 text-sm">Secure</p>
                  <p className="text-xs text-slate-500">Your data is protected</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}18` }}>
                  <Car className="w-5 h-5" style={{ color }} />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-slate-900 text-sm">Easy</p>
                  <p className="text-xs text-slate-500">Step-by-step guide</p>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col items-center gap-3">
              <Button
                onClick={onStartManual}
                className="text-white font-semibold py-4 px-8 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all w-full sm:w-auto"
                style={{ backgroundColor: color }}
                onMouseEnter={(e) => e.target.style.backgroundColor = hoverColor}
                onMouseLeave={(e) => e.target.style.backgroundColor = color}
                data-testid="start-application-btn"
              >
                Start Application
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              <TrustBadge className="mt-4" />
              {source.show_staff_upload !== false && (
                <button
                  onClick={onGetStarted}
                  className="text-xs text-slate-400 hover:text-slate-600 transition-colors mt-6 py-2 text-center"
                  data-testid="staff-upload-btn"
                >
                  Staff? Upload an existing proposal
                </button>
              )}
            </div>
          </div>
          
          {heroImage && (
          <div className="hidden lg:block">
            <img 
              src={heroImage}
              alt={`${companyName}`}
              className="rounded-2xl shadow-2xl w-full h-[500px] object-cover object-top"
              data-testid="hero-image"
            />
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MainApp = () => {
  const { source } = useSource();
  const [step, setStep] = useState('landing');
  const [enteredFormVia, setEnteredFormVia] = useState(null);

  const goTo = React.useCallback((newStep, fromStep) => {
    if (fromStep) setEnteredFormVia(fromStep);
    window.history.pushState({ step: newStep }, '');
    setStep(newStep);
  }, []);

  React.useEffect(() => {
    window.history.replaceState({ step: 'landing' }, '');
    const onPop = (e) => setStep(e.state?.step || 'landing');
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const renderContent = () => {
    switch (step) {
      case 'landing':
        return <LandingPage onGetStarted={() => goTo('upload', 'landing')} onStartManual={() => { trackManualStart(source.slug); goTo('form', 'landing'); }} />;
      case 'upload':
        return (
          <div className="max-w-3xl mx-auto px-4 py-8">
            <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
              <UploadStep onComplete={() => goTo('form', 'upload')} onSkip={() => goTo('form', 'upload')} />
            </div>
          </div>
        );
      case 'form':
        return (
          <div className="max-w-3xl mx-auto px-4 py-8">
            <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
              <FormWizard onBack={() => goTo(enteredFormVia || 'landing')} />
            </div>
          </div>
        );
      default:
        return <LandingPage onGetStarted={() => goTo('upload', 'landing')} onStartManual={() => goTo('form', 'landing')} />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-slate-50 to-green-50">
      <Header />
      <main className="flex-1" data-testid="main-content">
        {renderContent()}
      </main>
      <Footer />
    </div>
  );
};

// Source-branded entry — loads source config then renders MainApp
const SourceApp = ({ initialStep = 'landing' }) => {
  const { slug } = useParams();
  const [step, setStep] = useState(initialStep);
  const [enteredFormVia, setEnteredFormVia] = useState(null);

  const goTo = React.useCallback((newStep, fromStep) => {
    if (fromStep) setEnteredFormVia(fromStep);
    window.history.pushState({ step: newStep }, '');
    setStep(newStep);
  }, []);

  React.useEffect(() => {
    window.history.replaceState({ step: initialStep }, '');
    const onPop = (e) => setStep(e.state?.step || initialStep);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [initialStep]);

  return (
    <SourceProvider slug={slug}>
      <SourceAppInner step={step} setStep={setStep} goTo={goTo} enteredFormVia={enteredFormVia} />
    </SourceProvider>
  );
};

const SourceAppInner = ({ step, setStep, goTo, enteredFormVia }) => {
  const { source, loading, notFound } = useSource();

  // Set dynamic favicon and page title from source config
  React.useEffect(() => {
    if (!source) return;
    const iconUrl = source.og_icon_url || source.logo_url;
    const displayName = source.trading_name || source.name || source.company_name || 'ApplyDrive.uk';
    document.title = `Apply for Vehicle Finance - ${displayName}`;
    if (iconUrl) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
      link.href = iconUrl;
    }
    return () => { document.title = 'ApplyDrive.uk'; };
  }, [source]);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-green-50">
        <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
      </div>
    );
  }
  
  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-green-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Source Not Found</h1>
          <p className="text-slate-500">This application link is not valid or has been deactivated.</p>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    switch (step) {
      case 'landing':
        return <LandingPage onGetStarted={() => goTo('upload', 'landing')} onStartManual={() => { trackManualStart(source.slug); goTo('form', 'landing'); }} />;
      case 'upload':
        return (
          <div className="max-w-3xl mx-auto px-4 py-8">
            <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
              <UploadStep onComplete={() => goTo('form', 'upload')} onSkip={() => goTo('form', 'upload')} />
            </div>
          </div>
        );
      case 'form':
        return (
          <div className="max-w-3xl mx-auto px-4 py-8">
            <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
              <FormWizard onBack={() => goTo(enteredFormVia || 'landing')} onExportAutoFill={() => {}} />
            </div>
          </div>
        );
      default:
        return <LandingPage onGetStarted={() => goTo('upload', 'landing')} onStartManual={() => goTo('form', 'landing')} />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-slate-50 to-green-50">
      <Header />
      <main className="flex-1" data-testid="main-content">
        {renderContent()}
      </main>
      <Footer />
    </div>
  );
};

// Direct form entry component (no landing page, goes straight to form)
const DirectFormEntry = () => {
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-slate-50 to-green-50">
      <Header />
      <main className="flex-1" data-testid="main-content">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
            <FormWizard onBack={() => window.location.href = '/'} onExportAutoFill={() => {}} />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

const AdminRoute = () => {
  const { secret } = useParams();
  return <AdminDashboard secret={secret} />;
};

const SourcePrivacyRoute = () => {
  const { slug } = useParams();
  return (
    <SourceProvider slug={slug}>
      <SourcePrivacyInner />
    </SourceProvider>
  );
};

const SourcePrivacyInner = () => {
  const { source, loading } = useSource();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  return (
    <div className="min-h-screen flex flex-col" style={{ background: `linear-gradient(to bottom, #f8fafc, ${source.primary_color}10)` }}>
      <main className="flex-1"><PrivacyPolicy source={source} /></main>
    </div>
  );
};

function App() {
  return (
    <FormProvider>
      <SourceProvider slug={null}>
        <div className="App">
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<ApplyDriveLanding />} />
              <Route path="/apply" element={<DirectFormEntry />} />
              <Route path="/privacy" element={
                <div className="min-h-screen flex flex-col bg-gradient-to-b from-slate-50 to-purple-50">
                  <main className="flex-1"><PrivacyPolicy /></main>
                </div>
              } />
              <Route path="/s/:slug/privacy" element={<SourcePrivacyRoute />} />
              <Route path="/platform-notice" element={
                <div className="min-h-screen flex flex-col bg-gradient-to-b from-slate-50 to-purple-50">
                  <main className="flex-1"><PlatformNotice /></main>
                </div>
              } />
              <Route path="/admin/:secret/operator" element={<OperatorMode />} />
              <Route path="/admin/:secret" element={<AdminRoute />} />
              <Route path="/s/:slug/extract" element={<SourceApp initialStep="upload" />} />
              <Route path="/s/:slug" element={<SourceApp />} />
              <Route path="*" element={<ApplyDriveLanding />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
          <CookieConsent />
        </div>
      </SourceProvider>
    </FormProvider>
  );
}

export default App;
